/**
 * Atlas Kanban board (v2).
 *
 * Fully draggable:
 *  - Tasks: draggable between columns via task card body (dnd-kit).
 *  - Panels: STOPS / TEAM / OFFICE reorder via each panel's ⋮⋮ handle.
 *  - Within TEAM: BRIAN / DAD reorder via each column's handle.
 *  - Within OFFICE: TOTAL / middle-stack / CALLS + inner NOW/LATER reorder.
 *
 * Extras: tap urgency dot to cycle grey→green→yellow→red→grey (grey adds
 * strikethrough), per-column Trash bin (pale-red link left of the counter),
 * inline column + group rename.
 */
import { useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, Check as CheckIcon, X as XIcon, DotsSixVertical,
} from "@phosphor-icons/react";
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable, closestCenter, DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy, horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AtlasTrashModal from "@/components/atlas/AtlasTrashModal";

const URGENCY_ORDER = ["none", "green", "yellow", "red"];
const URGENCY_STYLES = {
  none:   { dot: "bg-slate-300", ring: "ring-slate-200",  pill: "bg-white",       border: "border-slate-200" },
  green:  { dot: "bg-emerald-500", ring: "ring-emerald-300", pill: "bg-emerald-50",  border: "border-emerald-300" },
  yellow: { dot: "bg-amber-500",  ring: "ring-amber-300",  pill: "bg-amber-50",    border: "border-amber-300" },
  red:    { dot: "bg-rose-500",   ring: "ring-rose-300",   pill: "bg-rose-50",     border: "border-rose-300" },
};

function nextUrgency(u) {
  const i = URGENCY_ORDER.indexOf(u || "none");
  return URGENCY_ORDER[(i + 1) % URGENCY_ORDER.length];
}

// Prefix used to distinguish panel-drag ids from task-drag ids in DndContext.
const PANEL_ID = (t, k) => `panel:${t}:${k}`;
const isPanelId = (id) => typeof id === "string" && id.startsWith("panel:");

export default function AtlasBoard({ board, loading, onReload }) {
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState([]);
  const [layout, setLayout] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [trashFor, setTrashFor] = useState(null); // { column }

  useMemo(() => {
    if (board) {
      setTasks(board.tasks || []);
      setColumns(board.columns || []);
      setLayout(board.layout || null);
    }
  }, [board]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const colByKey = useMemo(() => Object.fromEntries((columns || []).map((c) => [c.key, c])), [columns]);
  const teamGroupName = useMemo(() => (columns.find((c) => c.group === "team") || {}).group_name || "TEAM", [columns]);
  const officeGroupName = useMemo(() => (columns.find((c) => c.group === "office") || {}).group_name || "OFFICE", [columns]);

  const tasksByColumn = useMemo(() => {
    const map = {};
    (tasks || []).forEach((t) => { (map[t.column_key] ||= []).push(t); });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => a.order - b.order));
    return map;
  }, [tasks]);

  const findTask = (id) => tasks.find((t) => t.id === id);

  // ---- Layout helpers ----
  const topPanels = layout?.top_panels || ["stops", "team", "office"];
  const teamOrder = layout?.team_order || ["brian", "dad"];
  const officeOrder = layout?.office_order || ["total", "middle", "calls"];
  const officeMiddleOrder = layout?.office_middle_order || ["now", "later"];
  const teamOrientation = layout?.team_orientation || "horizontal";
  const officeMiddleOrientation = layout?.office_middle_orientation || "vertical";

  const persistLayout = async (patch) => {
    const next = { ...(layout || {}), ...patch };
    setLayout(next);
    try {
      await api.put("/atlas/layout", patch);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      onReload && onReload();
    }
  };

  // ---- Drag handlers ----
  const onDragStart = (evt) => {
    if (isPanelId(evt.active.id)) return;
    const t = findTask(evt.active.id);
    if (t) setActiveTask(t);
  };
  const onDragEnd = async (evt) => {
    setActiveTask(null);
    const { active, over } = evt;
    if (!over || active.id === over.id) return;

    // ---- Panel drags ----
    if (isPanelId(active.id) && isPanelId(over.id)) {
      const [, aType, aKey] = active.id.split(":");
      const [, bType, bKey] = over.id.split(":");
      if (aType !== bType) return;
      if (aType === "top") {
        const from = topPanels.indexOf(aKey);
        const to = topPanels.indexOf(bKey);
        if (from < 0 || to < 0 || from === to) return;
        const next = arrayMove(topPanels, from, to);
        persistLayout({ top_panels: next });
      } else if (aType === "team") {
        const from = teamOrder.indexOf(aKey);
        const to = teamOrder.indexOf(bKey);
        if (from < 0 || to < 0 || from === to) return;
        persistLayout({ team_order: arrayMove(teamOrder, from, to) });
      } else if (aType === "office") {
        const from = officeOrder.indexOf(aKey);
        const to = officeOrder.indexOf(bKey);
        if (from < 0 || to < 0 || from === to) return;
        persistLayout({ office_order: arrayMove(officeOrder, from, to) });
      } else if (aType === "officeMid") {
        const from = officeMiddleOrder.indexOf(aKey);
        const to = officeMiddleOrder.indexOf(bKey);
        if (from < 0 || to < 0 || from === to) return;
        persistLayout({ office_middle_order: arrayMove(officeMiddleOrder, from, to) });
      }
      return;
    }

    // ---- Task drags ----
    const src = findTask(active.id);
    if (!src) return;
    let targetCol = src.column_key;
    let targetIdx = 0;
    const overTask = findTask(over.id);
    if (overTask) {
      targetCol = overTask.column_key;
      const list = (tasksByColumn[targetCol] || []).map((t) => t.id);
      targetIdx = list.indexOf(over.id);
      if (targetCol === src.column_key) {
        const from = list.indexOf(src.id);
        if (from === targetIdx || targetIdx < 0) return;
        const newList = arrayMove(list, from, targetIdx);
        setTasks((prev) => {
          const map = new Map(prev.map((t) => [t.id, t]));
          newList.forEach((id, i) => { const t = map.get(id); if (t) map.set(id, { ...t, order: i }); });
          return Array.from(map.values());
        });
      }
    } else if (String(over.id).startsWith("col:")) {
      targetCol = String(over.id).slice(4);
      targetIdx = (tasksByColumn[targetCol] || []).length;
    } else {
      return;
    }
    setTasks((prev) => {
      const others = prev.filter((t) => t.id !== src.id);
      const moved = { ...src, column_key: targetCol, order: targetIdx };
      return [...others, moved];
    });
    try {
      await api.post(`/atlas/tasks/${src.id}/move`, { column_key: targetCol, order: targetIdx });
      onReload && onReload();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      onReload && onReload();
    }
  };

  // ---- Task actions ----
  const addTask = async (columnKey, text) => {
    if (!text || !text.trim()) return;
    try {
      const { data } = await api.post("/atlas/tasks", { column_key: columnKey, text: text.trim(), urgency: "green" });
      setTasks((prev) => [...prev, data]);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const updateTask = async (id, patch) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try { await api.put(`/atlas/tasks/${id}`, patch); } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      onReload && onReload();
    }
  };
  const cycleUrgency = (id) => {
    const t = tasks.find((x) => x.id === id);
    if (!t) return;
    updateTask(id, { urgency: nextUrgency(t.urgency) });
  };
  const deleteTask = async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    try { await api.delete(`/atlas/tasks/${id}`); } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      onReload && onReload();
    }
  };
  const restoreTask = async (id) => {
    try { await api.post(`/atlas/tasks/${id}/restore`); onReload && onReload(); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const renameColumn = async (key, name) => {
    setColumns((prev) => prev.map((c) => (c.key === key ? { ...c, name } : c)));
    try { await api.put(`/atlas/columns/${key}`, { name }); } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      onReload && onReload();
    }
  };
  const renameGroup = async (group, name) => {
    setColumns((prev) => prev.map((c) => (c.group === group ? { ...c, group_name: name } : c)));
    try { await api.put(`/atlas/groups/${group}`, { name }); } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
      onReload && onReload();
    }
  };

  if (loading) return <div className="text-sm text-slate-500 py-8 text-center">Loading Atlas…</div>;
  if (!board) return <div className="text-sm text-slate-500 py-8 text-center">Unable to load Atlas board.</div>;

  const colProps = { tasksByColumn, cycleUrgency, updateTask, deleteTask, addTask, renameColumn, openTrash: (col) => setTrashFor({ column: col }) };

  // Assemble every sortable id used anywhere in the tree so that the single
  // top-level DndContext can dispatch each drop to the correct axis
  // (top-level, team, office, office-middle) via ``onDragEnd`` above.
  const allSortableIds = [
    ...topPanels.map((k) => PANEL_ID("top", k)),
    ...teamOrder.map((k) => PANEL_ID("team", k)),
    ...officeOrder.map((k) => PANEL_ID("office", k)),
    ...officeMiddleOrder.map((k) => PANEL_ID("officeMid", k)),
    ...tasks.map((t) => t.id),
  ];

  const renderColumnPanel = (colKey, groupType, extraClass = "") => {
    const cc = colByKey[colKey];
    if (!cc) return null;
    return (
      <SortablePanel id={PANEL_ID(groupType, colKey)} key={colKey} className={extraClass}>
        {(dh) => <Column column={cc} {...colProps} testId={`atlas-col-${cc.key}`} dragHandle={dh} />}
      </SortablePanel>
    );
  };

  const renderTopPanel = (key) => {
    if (key === "stops") {
      return (
        <SortablePanel id={PANEL_ID("top", "stops")} key="stops">
          {(dragHandle) => {
            const c = colByKey.stops;
            if (!c) return null;
            return <Column column={c} {...colProps} testId={`atlas-col-${c.key}`} dragHandle={dragHandle} className="lg:min-h-[220px]" />;
          }}
        </SortablePanel>
      );
    }
    if (key === "team") {
      return (
        <SortablePanel id={PANEL_ID("top", "team")} key="team">
          {(dragHandle) => (
            <GroupPanel
              groupKey="team"
              name={teamGroupName}
              onRename={(n) => renameGroup("team", n)}
              dragHandle={dragHandle}
              orientation={teamOrientation}
              onToggleOrientation={() => persistLayout({ team_orientation: teamOrientation === "horizontal" ? "vertical" : "horizontal" })}
            >
              <SortableContext
                items={teamOrder.map((k) => PANEL_ID("team", k))}
                strategy={teamOrientation === "horizontal" ? horizontalListSortingStrategy : verticalListSortingStrategy}
              >
                <div className={teamOrientation === "horizontal" ? "grid grid-cols-1 sm:grid-cols-2 gap-2" : "flex flex-col gap-2"}>
                  {teamOrder.map((k) => renderColumnPanel(k, "team"))}
                </div>
              </SortableContext>
            </GroupPanel>
          )}
        </SortablePanel>
      );
    }
    if (key === "office") {
      return (
        <SortablePanel id={PANEL_ID("top", "office")} key="office" className="lg:col-span-2">
          {(dragHandle) => (
            <GroupPanel
              groupKey="office"
              name={officeGroupName}
              onRename={(n) => renameGroup("office", n)}
              dragHandle={dragHandle}
              orientation={officeMiddleOrientation}
              onToggleOrientation={() => persistLayout({ office_middle_orientation: officeMiddleOrientation === "vertical" ? "horizontal" : "vertical" })}
              orientationLabel="MIDDLE"
            >
              <SortableContext
                items={officeOrder.map((k) => PANEL_ID("office", k))}
                strategy={horizontalListSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  {officeOrder.map((slot) => {
                    if (slot === "middle") {
                      return (
                        <SortablePanel id={PANEL_ID("office", "middle")} key="middle" className="md:col-span-2">
                          {(dh) => (
                            <div className={`relative rounded-lg border border-dashed border-slate-200 pt-3 px-1 pb-1 ${officeMiddleOrientation === "vertical" ? "flex flex-col gap-2" : "grid grid-cols-1 sm:grid-cols-2 gap-2"}`}>
                              {/* Middle-slot drag handle sits on the LEFT edge as a
                                  small pill so it doesn't visually collide with the
                                  drag handle inside the NOW column below it. */}
                              <div
                                className="absolute -top-2 left-2 z-10 bg-white rounded-full border border-slate-200 shadow-sm px-1"
                                {...dh.attributes}
                                {...dh.listeners}
                                ref={dh.setActivatorNodeRef}
                                data-testid="atlas-office-middle-handle"
                              >
                                <DragHandleIcon dragging={dh.isDragging} />
                              </div>
                              <SortableContext
                                items={officeMiddleOrder.map((k) => PANEL_ID("officeMid", k))}
                                strategy={officeMiddleOrientation === "vertical" ? verticalListSortingStrategy : horizontalListSortingStrategy}
                              >
                                {officeMiddleOrder.map((k) => renderColumnPanel(k, "officeMid"))}
                              </SortableContext>
                            </div>
                          )}
                        </SortablePanel>
                      );
                    }
                    return renderColumnPanel(slot, "office");
                  })}
                </div>
              </SortableContext>
            </GroupPanel>
          )}
        </SortablePanel>
      );
    }
    return null;
  };

  return (
    <>
      {/* One DndContext to rule them all — nested SortableContexts share it. */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <SortableContext items={allSortableIds}>
          <SortableContext items={topPanels.map((k) => PANEL_ID("top", k))} strategy={horizontalListSortingStrategy}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="atlas-board">
              {topPanels.map((k) => renderTopPanel(k))}
            </div>
          </SortableContext>
        </SortableContext>
        <DragOverlay>
          {activeTask ? (
            <TaskCard task={activeTask} dragging cycleUrgency={() => {}} updateTask={() => {}} deleteTask={() => {}} />
          ) : null}
        </DragOverlay>
      </DndContext>
      <AtlasTrashModal
        open={!!trashFor}
        column={trashFor?.column}
        onClose={() => setTrashFor(null)}
        onRestore={restoreTask}
      />
    </>
  );
}

// ---------------------------------------------------------------
// Small components
// ---------------------------------------------------------------
function DragHandleIcon({ dragging }) {
  return (
    <span
      className={`inline-flex items-center justify-center h-7 w-7 rounded-md touch-none select-none cursor-grab active:cursor-grabbing transition-colors ${
        dragging
          ? "bg-[#1f64c2] text-white ring-2 ring-[#327de3]/40"
          : "text-slate-400 hover:text-slate-700 hover:bg-slate-100 active:bg-slate-200 active:text-slate-800"
      }`}
      aria-label="Drag to reorder"
    >
      <DotsSixVertical size={16} weight="bold" />
    </span>
  );
}

function SortablePanel({ id, children, className = "" }) {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners, setActivatorNodeRef } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    // Smoother animation than dnd-kit's default (200 ms easeInOut)
    transition: transition || "transform 220ms cubic-bezier(0.2, 0, 0, 1)",
    opacity: isDragging ? 0.9 : 1,
    zIndex: isDragging ? 30 : "auto",
  };
  const dh = { attributes, listeners, setActivatorNodeRef, isDragging };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${className} ${isDragging ? "scale-[1.02] shadow-2xl ring-2 ring-[#1f64c2]/50 rounded-xl" : ""}`}
    >
      {children(dh)}
    </div>
  );
}

function GroupPanel({ groupKey, name, onRename, dragHandle, orientation, onToggleOrientation, orientationLabel = "STACK", children }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const commit = () => {
    const v = (draft || "").trim();
    if (v && v !== name) onRename(v);
    setEditing(false);
  };
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white/60 backdrop-blur-sm p-2 shadow-sm"
      data-testid={`atlas-group-${groupKey}`}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        {editing ? (
          <div className="flex items-center gap-1 flex-1">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
              className="border border-slate-300 rounded px-2 py-0.5 text-[11px] font-black uppercase tracking-[0.2em]"
              data-testid={`atlas-group-${groupKey}-rename-input`}
            />
            <button onClick={commit} className="text-emerald-600" data-testid={`atlas-group-${groupKey}-rename-save`}><CheckIcon size={12} weight="bold" /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400"><XIcon size={12} weight="bold" /></button>
          </div>
        ) : (
          <>
            <h3
              onDoubleClick={() => { setDraft(name); setEditing(true); }}
              className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 cursor-pointer select-none"
              data-testid={`atlas-group-${groupKey}-header`}
            >
              {name}
            </h3>
            <button
              onClick={() => { setDraft(name); setEditing(true); }}
              className="text-slate-300 hover:text-slate-600"
              data-testid={`atlas-group-${groupKey}-rename-btn`}
            >
              <PencilSimple size={10} />
            </button>
            {onToggleOrientation && (
              <button
                type="button"
                onClick={onToggleOrientation}
                className="ml-1 text-[9px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-700 px-1.5 py-0.5 rounded bg-white border border-slate-200"
                title="Toggle stack direction"
                data-testid={`atlas-group-${groupKey}-orientation`}
              >
                {orientation === "horizontal" ? `${orientationLabel} ↔` : `${orientationLabel} ↕`}
              </button>
            )}
            <div className="ml-auto" {...(dragHandle?.attributes || {})} {...(dragHandle?.listeners || {})} ref={dragHandle?.setActivatorNodeRef}>
              <DragHandleIcon dragging={dragHandle?.isDragging} />
            </div>
          </>
        )}
      </div>
      {children}
    </div>
  );
}

function Column({
  column, tasksByColumn, cycleUrgency, updateTask, deleteTask, addTask, renameColumn, openTrash,
  className = "", testId, dragHandle,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${column.key}` });
  const list = tasksByColumn[column.key] || [];
  const [newText, setNewText] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(column.name);

  const commitAdd = () => { const t = newText.trim(); if (!t) return; addTask(column.key, t); setNewText(""); };
  const commitRename = () => { const v = (nameDraft || "").trim(); if (v && v !== column.name) renameColumn(column.key, v); setRenaming(false); };

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border ${isOver ? "border-[#327de3] bg-[#327de3]/5" : "border-slate-200 bg-white"} p-2 shadow-sm ${className}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1 mb-1.5">
        {renaming ? (
          <>
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(false); }}
              className="border border-slate-300 rounded px-2 py-0.5 text-xs font-black uppercase tracking-widest w-full"
              data-testid={`${testId}-rename-input`}
            />
            <button onClick={commitRename} className="text-emerald-600" data-testid={`${testId}-rename-save`}><CheckIcon size={12} weight="bold" /></button>
            <button onClick={() => setRenaming(false)} className="text-slate-400"><XIcon size={12} weight="bold" /></button>
          </>
        ) : (
          <>
            <h4
              onDoubleClick={() => { setNameDraft(column.name); setRenaming(true); }}
              className="text-[11px] font-black uppercase tracking-widest text-slate-700 cursor-pointer select-none"
              data-testid={`${testId}-header`}
            >
              {column.name}
            </h4>
            <button
              onClick={() => { setNameDraft(column.name); setRenaming(true); }}
              className="text-slate-300 hover:text-slate-600"
              data-testid={`${testId}-rename-btn`}
            >
              <PencilSimple size={10} />
            </button>
            <button
              type="button"
              onClick={() => openTrash && openTrash(column)}
              className="ml-auto text-[10px] font-semibold text-rose-400 hover:text-rose-600 uppercase tracking-wide"
              data-testid={`${testId}-trash-link`}
            >
              trash
            </button>
            <span className="text-[10px] tabular-nums text-slate-400 ml-1" data-testid={`${testId}-count`}>{list.length}</span>
            <div {...(dragHandle?.attributes || {})} {...(dragHandle?.listeners || {})} ref={dragHandle?.setActivatorNodeRef}>
              <DragHandleIcon dragging={dragHandle?.isDragging} />
            </div>
          </>
        )}
      </div>

      <SortableContext items={list.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1 min-h-[28px]">
          {list.map((t) => (
            <SortableTask key={t.id} task={t} cycleUrgency={cycleUrgency} updateTask={updateTask} deleteTask={deleteTask} />
          ))}
          {list.length === 0 && (
            <div className="text-[11px] text-slate-300 italic px-1 py-0.5">Drop tasks here</div>
          )}
        </div>
      </SortableContext>

      <div className="mt-1.5 flex items-center gap-1">
        <input
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commitAdd(); }}
          placeholder="— add task"
          className="flex-1 text-[12px] px-2 py-1 rounded-md border border-slate-200 focus:border-[#327de3] focus:outline-none placeholder:text-slate-300"
          data-testid={`${testId}-add-input`}
        />
        <button type="button" onClick={commitAdd} className="text-slate-400 hover:text-[#327de3]" data-testid={`${testId}-add-btn`}>
          <Plus size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function SortableTask({ task, cycleUrgency, updateTask, deleteTask }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCard task={task} cycleUrgency={cycleUrgency} updateTask={updateTask} deleteTask={deleteTask} />
    </div>
  );
}

function TaskCard({ task, dragging, cycleUrgency, updateTask, deleteTask }) {
  const s = URGENCY_STYLES[task.urgency] || URGENCY_STYLES.none;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.text);
  const commit = () => { const v = (draft || "").trim(); if (v && v !== task.text) updateTask(task.id, { text: v }); setEditing(false); };
  const isNone = (task.urgency || "none") === "none";
  return (
    <div
      className={`group flex items-start gap-1.5 rounded-md border ${s.border} ${s.pill} px-1.5 py-1 text-[12px] leading-snug ${dragging ? "shadow-lg" : "hover:shadow-sm"}`}
      data-testid={`atlas-task-${task.id}`}
    >
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); cycleUrgency(task.id); }}
        title="Cycle urgency"
        className={`shrink-0 mt-[3px] w-4 h-4 rounded-full flex items-center justify-center ${isNone ? "text-slate-400" : ""}`}
        data-testid={`atlas-task-${task.id}-urgency`}
      >
        {isNone ? (
          <span className="text-[10px] font-black tracking-[0.05em] leading-none">---</span>
        ) : (
          <span className={`w-3 h-3 rounded-full ring-2 ${s.dot} ${s.ring}`} />
        )}
      </button>

      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          onBlur={commit}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 border border-slate-300 rounded px-1 py-0.5 text-[12px]"
          data-testid={`atlas-task-${task.id}-input`}
        />
      ) : (
        <div
          className={`flex-1 min-w-0 break-words cursor-text ${isNone ? "text-slate-500 line-through decoration-slate-400" : "text-slate-800"}`}
          onDoubleClick={(e) => { e.stopPropagation(); setDraft(task.text); setEditing(true); }}
          data-testid={`atlas-task-${task.id}-text`}
        >
          {task.text}
        </div>
      )}

      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }}
        title="Delete (moves to Trash bin)"
        className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500 transition-opacity"
        data-testid={`atlas-task-${task.id}-delete`}
      >
        <Trash size={11} />
      </button>
    </div>
  );
}
